import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { faroTableComparator } from "@components/common/faro-table/faro-table-utils";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { BasicInfoMemberColumn } from "@components/table/members/members-column/basic-info-member-column";
import { OptionsMemberColumn } from "@components/table/members/members-column/options-member-column";
import { RoleColumn } from "@components/table/members/members-column/role-column/role-column";
import { StatusColumn } from "@components/table/members/members-column/status-column/status-column";
import {
  MemberHeaders,
  getRoleHeader,
} from "@components/table/members/members-table-utils";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { isGroupMember } from "@custom-types/member-type-guards";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { FormatDate } from "@hooks/use-date-time";
import {
  GridColDef,
  GridRenderCellParams,
  gridStringOrNumberComparator,
} from "@mui/x-data-grid-pro";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { withEllipsis } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";
import { isTeam } from "@utils/team-utils";
import { DASH } from "@utils/ui-utils";
import { getMemberStatus } from "@utils/user-utils";

interface Props extends BaseCompanyIdProps {
  /** The selected project if there is one */
  project?: SdbProject | null;

  /** Recognizes on which subject the table is being used */
  tableSubject: MemberTableSubject;

  /** The selected group if there is one */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;

  /** The selected team if there is one */
  team?: FaroCoreApiTypes.Team;

  /** The function to format date. Needs to be passed since getMembersColumns is not a hook */
  formatDate: FormatDate;

  options: {
    /** Whether the current screen is large and larger */
    isScreenLgOrLarger: boolean;

    /** Whether the current screen is medium */
    isScreenMd: boolean;
  };
}

/** Minimum column width in px */
const COLS_MIN_WIDTH_IN_PX = 150;

/**
 * Columns width in xs and sm screen
 */
const xsAndSmColumnsWidthPX = 200;

/**
 * like name, email, status and joined date in medium screens
 */
const mdColumnsWidthInPX = 250;

/** Return all the possible columns for members table */
export function getMembersColumns({
  companyId,
  project,
  tableSubject,
  group,
  team,
  formatDate,
  options,
}: Props): Record<MemberHeaders, GridColDef> {
  return {
    [MemberHeaders.avatar]: {
      field: MemberHeaders.avatar,
      headerName: "",
      width: 70,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => <UserAvatarWithMenu member={params.row.entity} />,
    },

    [MemberHeaders.name]: {
      field: MemberHeaders.name,
      flex: options.isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      maxWidth: options.isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => (
        <BasicInfoMemberColumn
          member={params.row.entity}
          columnType={MemberHeaders.name}
        />
      ),
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity.name,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [MemberHeaders.user]: {
      field: MemberHeaders.user,
      flex: options.isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      maxWidth: options.isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => (
        <BasicInfoMemberColumn
          member={params.row.entity}
          columnType={MemberHeaders.user}
        />
      ),
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity.name,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [MemberHeaders.email]: {
      field: MemberHeaders.email,
      flex: options.isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      width: options.isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => (
        <BasicInfoMemberColumn
          member={params.row.entity}
          columnType={MemberHeaders.email}
        />
      ),
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) => {
        const teamComparison = Number(isTeam(v1)) - Number(isTeam(v2));

        if (teamComparison !== 0) {
          return teamComparison;
        }

        return gridStringOrNumberComparator(v1.email, v2.email, cp1, cp2);
      },
    },

    [MemberHeaders.status]: {
      field: MemberHeaders.status,
      flex: options.isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      width: options.isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        return isTeam(params.row.entity) ? (
          <var>{DASH}</var>
        ) : (
          <StatusColumn
            companyId={companyId}
            project={project}
            subjectType={tableSubject}
            member={params.row.entity}
          />
        );
      },
      valueGetter: (_, row: { entity: SdbMemberTypes }) => row.entity,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) => {
        const status1 = getMemberStatus({ member: v1 });
        const status2 = getMemberStatus({ member: v2 });

        // Ensures the result is numeric
        const teamComparison = Number(isTeam(v1)) - Number(isTeam(v2));

        if (teamComparison !== 0) {
          return teamComparison;
        }

        return gridStringOrNumberComparator(status1, status2, cp1, cp2);
      },
    },

    [MemberHeaders.role]: {
      field: MemberHeaders.role,
      flex: options.isScreenLgOrLarger ? 1 : undefined,
      headerName: getRoleHeader(tableSubject),
      minWidth: COLS_MIN_WIDTH_IN_PX,
      maxWidth: options.isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        return (
          <RoleColumn
            companyId={companyId}
            selectedProject={project}
            subjectType={tableSubject}
            memberIdentityRole={{
              identity: params.row.entity.identity,
              role: params.row.entity.role,
            }}
            group={group}
          />
        );
      },
      valueGetter: (_, row: { entity: SdbMemberTypes }) =>
        formatUserRoleType(row.entity.role),
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [MemberHeaders.joinedDate]: {
      field: MemberHeaders.joinedDate,
      flex: options.isScreenLgOrLarger ? 1 : undefined,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      width: options.isScreenMd ? mdColumnsWidthInPX : xsAndSmColumnsWidthPX,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        if (isGroupMember(params.row.entity)) {
          return (
            <SphereTooltip
              boxProps={{ sx: withEllipsis }}
              shouldShowTooltip={false}
              title={params.row.entity.dateJoined}
            >
              <var>{formatDate(params.row.entity.dateJoined)}</var>
            </SphereTooltip>
          );
        }
      },
      valueGetter: (_, row: { entity: SdbMemberTypes }) =>
        isGroupMember(row.entity) ? row.entity.dateJoined : undefined,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) =>
        faroTableComparator<string>(
          v1,
          v2,
          cp1,
          cp2,
          gridStringOrNumberComparator
        ),
    },

    [MemberHeaders.options]: {
      field: MemberHeaders.options,
      type: "actions",
      align: "right",
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- This is needed for the flex to work properly
      flex: options.isScreenLgOrLarger ? 0.5 : undefined,
      width: 50,
      renderCell: (
        params: GridRenderCellParams<{ entity: SdbMemberTypes }>
      ) => {
        return (
          <OptionsMemberColumn
            companyId={companyId}
            selectedProject={project}
            subjectType={tableSubject}
            member={params.row.entity}
            group={group}
            team={team}
          />
        );
      },
    },
  };
}
